import React, {useCallback, useContext, useEffect, useState} from 'react';
import CustomList from "../../../../components/shared/List";
import { PaginationContext } from "../../../../components/shared/pagination/PaginationContext";
import {getFilterMeta} from "./FilterMeta";
import { withApollo } from "react-apollo";
import {LIST_USERS_DISPLAYS, LIST_USERS_FILTERS} from "../../../../gql/bookingapi";
import { OwcPagination, OwcProgressSpinner } from "@one/react";
import {useSelector} from "react-redux";

export const emptyValueSign = "-";

const FilterContent = ({ client, registerUpdateFn, registerTriggerFetch, showMyFilters = false }) => {
  const { fetching } = useContext(PaginationContext);
  const [filtersList, setFiltersList] = useState([]);
  const [allFiltersList, setAllFiltersList] = useState([]);
  const [pageNo, setPageNo] = useState(1);
  const [showPerPage, setShowPerPage] = useState(5);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(true);
  const [triggerRefresh, setTriggerRefresh] = useState(() => () => {});

  const storeUser = useSelector((store) => store.user);

  const fetchFilters = useCallback(async () => {
    setLoading(true);
    try {
      const [filtersResponse, displaysResponse] = await Promise.all([
        client.query({
          query: LIST_USERS_FILTERS,
          variables: { siteName: storeUser.site },
          fetchPolicy: 'network-only',
        }),
        client.query({
          query: LIST_USERS_DISPLAYS,
          variables: { siteName: storeUser.site, limit: 1000 },
          fetchPolicy: 'network-only',
        }),
      ]);

      const allFilters = filtersResponse?.data?.listAllUsersFilters ?? [];
      const allDisplays = displaysResponse?.data?.listAllUsersDisplays ?? [];

      const filtersByAssignment = new Map(
        allFilters
          .filter(f => f.assignmentIdentifier)
          .map(f => [f.assignmentIdentifier, f])
      );

      const assignmentRows = [];

      for (const filter of allFilters) {
        if (!filter.sharedWithGroups || !filter.sharedWithGroups.length) continue;

        const matchingDisplay = allDisplays.find(
          display => display.assignmentIdentifier === filter.assignmentIdentifier
        );

        assignmentRows.push({
          ...filter,
          columnSetOwnerId: matchingDisplay?.userID ?? null,
          columnSet: matchingDisplay?.displayName ?? emptyValueSign,
          assignedGroups: filter.sharedWithGroups.join(", "),
          key: filter.id,
        });
      }

      for (const display of allDisplays) {
        if (!display.sharedWithGroups || !display.sharedWithGroups.length) continue;
        if (filtersByAssignment.has(display.assignmentIdentifier)) continue; // already handled above
        assignmentRows.push({
          createdBy: display.createdBy,
          modifiedBy: display.modifiedBy,
          id: display.id,
          userID: display.userID,
          filterName: emptyValueSign,
          filterModel: emptyValueSign,
          columnSetOwnerId: display.userID,
          columnSet: display.displayName ?? emptyValueSign,
          assignedGroups: display.sharedWithGroups.join(", "),
          sharedWithGroups: display.sharedWithGroups,
          key: display.id,
        });
      }

      const visible = showMyFilters
        ? assignmentRows.filter(
          item => item.userID === storeUser.id || item.columnSetOwnerId === storeUser.id
        )
        : assignmentRows;

      setAllFiltersList(visible);
      setTotalCount(visible.length);
      paginate(visible);
    } catch (error) {
      console.log("Failed to fetch filters:", error);
      setAllFiltersList([]);
      setFiltersList([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMyFilters]);

  useEffect(() => {
    if (registerTriggerFetch) {
      registerTriggerFetch(fetchFilters);
    }
    setTriggerRefresh(() => fetchFilters); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerTriggerFetch, fetchFilters]);

  useEffect(() => {
    fetchFilters(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, showMyFilters]);

  const paginate = (source) => {
    setVisible(false);
    setLoading(true);
    setTimeout(() => {
      const lowIndex = (pageNo - 1) * showPerPage;
      const highIndex = lowIndex + showPerPage;
      setFiltersList(source.slice(lowIndex, highIndex));
      setVisible(true);
      setLoading(false);
    })
  };

  useEffect(() => {
    paginate(allFiltersList) // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNo, showPerPage, allFiltersList]);

  const updateSharedGroups = (filterId, newGroups) => {
    setAllFiltersList(prev => {
      return prev.map(f =>
        `EQUIPMENTLIST#${f.filterName}` === filterId
          ? {
            ...f,
            sharedWithGroups: newGroups,
            assignedGroups: newGroups?.length ? newGroups.join(", ") : emptyValueSign,
            modifiedBy: storeUser.name || storeUser.email || storeUser.id
          }
          : f
      );
    });
  };

  useEffect(() => {
    if (typeof registerUpdateFn === 'function') {
      registerUpdateFn(updateSharedGroups);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {loading ? <div style={{ minHeight: '40px' }}>
        <OwcProgressSpinner />
      </div> : null}
      {visible && (
        <CustomList
          meta={getFilterMeta(triggerRefresh, setTriggerRefresh)}
          data={filtersList}
          fieldArray={[]}
          onRequestSort={false}
          isReverseOrder={true}
          orderBy="name"
          loading={fetching}/>
      )}
      <OwcPagination
        onPageChange={(e) => {
          setShowPerPage(e.detail.rowsPerPage);
          setPageNo(e.detail.page);
        }}
        options={[5, 10, 20, 50]}
        rowsPerPage={showPerPage}
        total={Math.ceil(totalCount / showPerPage)}
      >
        <span slot="rows-per-page">Rows per page</span>
      </OwcPagination>

    </>
  );
};

export default withApollo(FilterContent);
